import React from "react";
import { NX_SERVICES, NX_MAIN_SERVICES } from "./consult-data.jsx";
import { NoriMark, TypingDots, useNXTypedPlaceholder } from "./consult-primitives.jsx";
import { readConsultSeed } from "./consult-seed.js";
import { NX_API, consultationReplyMetadata, serializeConsultantFormAnswers } from "./consult-client.js";
import { MOBILE_MAX_WIDTH, consultFieldFontSize } from "./consult-presentation.js";

const ConsultMarkdown = React.lazy(() => import("./consult-markdown-view.jsx"));

// The Service Focus ids a seed may name. Anything else is not a focus.
const NX_SERVICE_IDS = NX_SERVICES.map((s) => s.id);

// Starter Questions (#72): hardcoded, English only. Each may name a Service Focus.
const NX_STARTER_QUESTIONS = [
  { text: "Which AI solution fits my business?", focus: "ai" },
  { text: "How fast can you build an MVP?", focus: "product" },
  { text: "Can you make my website AI-ready?", focus: "ai-ready-web" },
  { text: "What does NEXT INNOVATIONS do?", focus: null },
];

const { useState: useState_Pill, useRef: useRef_Pill, useEffect: useEffect_Pill } = React;

function useViewportWidth() {
  const read = () => (typeof window === "undefined" ? undefined : window.innerWidth);
  const [width, setWidth] = useState_Pill(read);
  useEffect_Pill(() => {
    const update = () => setWidth(read());
    update();
    window.addEventListener("resize", update, { passive: true });
    return () => window.removeEventListener("resize", update);
  }, []);
  return width;
}

// Font size for a Consultant text control: `desktopSize` above the mobile
// breakpoint, 16px at or below it so mobile browsers don't zoom on focus.
function useConsultFieldFontSize(desktopSize) {
  return consultFieldFontSize(useViewportWidth(), desktopSize);
}

/* ───── Service icons (tiny, drawn — match NI visual vocab) ───── */
function ServiceIcon({ id, size = 16, color = "currentColor" }) {
  const s = size;
  const props = { width: s, height: s, viewBox: "0 0 20 20", fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (id === "ai") return (
    <svg {...props}><circle cx="10" cy="10" r="7" /><path d="M7 8.5h1.5M11.5 8.5H13M7 12c.8.6 1.8 1 3 1s2.2-.4 3-1" /></svg>
  );
  if (id === "product") return (
    <svg {...props}><path d="M10 2.5l1.6 1.6c2 2 2 4.8 0 6.8L10 12.5l-1.6-1.6c-2-2-2-4.8 0-6.8L10 2.5z" /><circle cx="10" cy="6.5" r="1" fill={color} /><path d="M7 14l-2 3M13 14l2 3M10 14v3" /></svg>
  );
  if (id === "web") return (
    <svg {...props}><rect x="2.5" y="3.5" width="15" height="11" rx="1.5" /><path d="M2.5 7h15M5 5h.5M7 5h.5M9 5h.5M7 17h6M10 14.5V17" /></svg>
  );
  if (id === "smartpost") return (
    <svg {...props}><path d="M3 5l11-2v14L3 15z" /><path d="M3 9.5l11-1.5M7 17l-1-2.3" /></svg>
  );
  if (id === "talent") return (
    <svg {...props}><circle cx="7" cy="7.5" r="2.5" /><circle cx="13.5" cy="8" r="2" /><path d="M2.5 16c.5-2.4 2.3-4 4.5-4s4 1.6 4.5 4M11.5 14.5c.6-1.5 2-2.5 3.5-2.5s2.9 1 3.5 2.5" /></svg>
  );
  if (id === "tiktok") return (
    <svg {...props}><rect x="5.5" y="2.5" width="9" height="15" rx="2" /><circle cx="10" cy="14.5" r="1" fill={color} /><path d="M9 6h2M13 5l1 1-1 1" /></svg>
  );
  return <svg {...props}><circle cx="10" cy="10" r="6" /></svg>;
}

/* ───── The chosen launcher ───────────────────────────────────── */

function PillLauncher({ open, onToggle, accentName = "Consultant", hasMessage = false, resumable = false }) {
  const compact = useViewportWidth() <= MOBILE_MAX_WIDTH;

  if (open) return null;

  return (
    <div style={{
        position: "absolute",
        bottom: compact ? 14 : 24,
        left: "auto",
        right: compact ? 14 : 24,
        transform: "none",
        zIndex: 4,
      }}>
        <button
          type="button"
          aria-label={resumable
            ? `Reopen Consultation${hasMessage ? ", unread response" : ""}`
            : `Ask ${accentName}`}
          className={"nx-ai-launcher" + (hasMessage ? " has-message" : "")}
          onClick={onToggle}
          style={{
            all: "unset",
            // The full-screen floating host lets clicks pass through to the page.
            pointerEvents: "auto",
            display: "flex", alignItems: "center",
            width: compact ? 72 : 188,
            height: compact ? 42 : 48,
            padding: compact ? "0 5px 0 10px" : "0 6px 0 18px",
            boxSizing: "border-box",
            background: "rgba(255, 252, 246, 0.92)",
            backdropFilter: "blur(14px) saturate(140%)",
            WebkitBackdropFilter: "blur(14px) saturate(140%)",
            border: "1px solid var(--line)",
            borderRadius: 999,
            boxShadow: "0 10px 28px -10px rgba(14,18,23,0.18), 0 2px 6px -2px rgba(14,18,23,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
            cursor: "pointer",
          }}
        >
          <span className="nx-ai-launcher-scan" aria-hidden="true" />
          <span className="nx-ai-launcher-dot" aria-hidden="true" />
          <span style={{
            fontSize: compact ? 12 : 14,
            color: "var(--fg-muted)", fontFamily: "var(--font-sans)",
            flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {compact ? "Consultant" : (resumable ? "Reopen Consultation" : `Ask ${accentName}…`)}
          </span>
          <span className="nx-ai-launcher-orb" aria-hidden="true" style={{
            width: compact ? 30 : 32,
            height: compact ? 30 : 32,
            borderRadius: 999,
            background: "var(--paper-3, #ECE6DA)", color: "var(--fg)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, flexShrink: 0,
          }}>↑</span>
        </button>
    </div>
  );
}

/* ───── The chat panel ───────────────────────────────────────── */

function ConsultPanel({ initialState = "greet", consultationState = null, onConsultationStateChange, onMinimize, onComposerFocus, onComposerBlur, onResponseComplete, accentName = "Consultant", closing = false, minimized = false, presentationMode = "collapsed", logoSrc = "mqahn3yh-company-logo-removebg-preview.png", draftRequest = null, readOnly = false, notice = "", onStartNew, onSuccessfulSubmit, onExpired }) {
  // The floating owner keeps transcript lifecycle above this mount boundary.
  const seeded = consultationState || seedTranscript(initialState);
  const consultSeedRef = useRef_Pill(
    readConsultSeed(
      typeof window !== "undefined" ? window.__NX_CONSULT_SEED : null,
      NX_SERVICE_IDS,
    ),
  );
  const [msgs, setMsgs]   = useState_Pill(seeded.msgs || []);
  const [focus, setFocus] = useState_Pill(consultSeedRef.current?.focus || seeded.focus || null);
  const [draft, setDraft] = useState_Pill("");
  const [busy, setBusy]   = useState_Pill(false);
  const [waiting, setWaiting] = useState_Pill(false);
  const [stage, setStage] = useState_Pill(seeded.stage); // "greet" | "discover" | "recommend" | "handoff"
  // Starter Questions show only on an empty transcript with no external seed
  // pending on mount; once tapped, the siblings fade for a beat before leaving.
  const [startersLeaving, setStartersLeaving] = useState_Pill(false);
  const showStarters = !consultSeedRef.current && (msgs.length === 0 || startersLeaving);
  useEffect_Pill(() => {
    if (!startersLeaving) return undefined;
    const t = setTimeout(() => setStartersLeaving(false), 150);
    return () => clearTimeout(t);
  }, [startersLeaving]);
  const scrollRef = useRef_Pill(null);
  const composerRef = useRef_Pill(null);
  const turnIdRef = useRef_Pill(0);
  const abortRef = useRef_Pill(null);

  // States this panel reported upward. The owner echoes them back as the
  // consultationState prop one render later, so adopting an echo would revert
  // a newer streamed update and ping-pong with the owner's queue forever.
  const reportedStatesRef = useRef_Pill(new WeakSet());

  useEffect_Pill(() => {
    const state = { msgs, focus, stage };
    reportedStatesRef.current.add(state);
    onConsultationStateChange?.(state);
  }, [msgs, focus, stage, onConsultationStateChange]);

  useEffect_Pill(() => {
    if (!consultationState || reportedStatesRef.current.has(consultationState)) return;
    setMsgs(consultationState.msgs || []);
    setFocus(consultationState.focus || null);
    setStage(consultationState.stage);
  }, [consultationState]);

  const pinToBottom = () => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; };
  // Auto-scroll on new message
  useEffect_Pill(pinToBottom, [msgs, busy]);

  // Keep the list pinned to the bottom while the panel's size transition
  // (.nx-consult-panel in styles.css, --dur-slow) runs; shrinking would
  // otherwise leave scrollTop behind and drift the last message out of view.
  useEffect_Pill(() => {
    if (typeof requestAnimationFrame !== "function") { pinToBottom(); return; }
    const until = Date.now() + 600; // ponytail: fixed window > --dur-slow; read transitionend if the token ever grows
    let raf;
    const tick = () => { pinToBottom(); if (Date.now() < until) raf = requestAnimationFrame(tick); };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [presentationMode]);

  // If a page entry point left a seed turn, send it once on mount.
  useEffect_Pill(() => {
    const seed = consultSeedRef.current;
    if (seed && initialState === "greet") {
      try { window.__NX_CONSULT_SEED = null; } catch (_) {}
      if (seed.focus) setFocus(seed.focus);
      // Defer one tick so msgs/state are set first. The focus goes on the call
      // rather than being read off state, which this closure captured empty.
      setTimeout(() => send(seed.text, { focusService: seed.focus }), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft events never call send(). Preserve a draft already typed in the
  // panel and only hydrate an empty composer from the external entry point.
  useEffect_Pill(() => {
    if (!draftRequest) return undefined;
    const incomingDraft = typeof draftRequest.draft === "string"
      ? draftRequest.draft.trim().slice(0, 320)
      : "";
    if (incomingDraft) {
      setDraft((currentDraft) => currentDraft.trim() ? currentDraft : incomingDraft);
    }
    if (!draftRequest.focusComposer) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (composerRef.current) composerRef.current.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [draftRequest?.id]);

  useEffect_Pill(() => () => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const pushUser = (content) => {
    setMsgs((m) => [...m, { role: "user", content }]);
  };
  const pushNori = (content, extra = {}) => {
    setMsgs((m) => [...m, { role: "assistant", content, ...extra }]);
  };

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setWaiting(false);
  };

  const startNewConsultation = () => {
    const starter = seedTranscript("greet").msgs;
    const hasConsultationMessages = msgs.length !== starter.length || msgs.some((message, index) =>
      message.role !== starter[index]?.role || message.content !== starter[index]?.content || message.form,
    );
    if (hasConsultationMessages && !window.confirm("Start a new Consultation? Your current Consultation will remain in Hermes history.")) return;
    abortRef.current?.abort();
    abortRef.current = null;
    if (onStartNew) {
      onStartNew();
      return;
    }
    NX_API.startNewConsultation();
    const fresh = seedTranscript("greet");
    setMsgs(fresh.msgs);
    setFocus(fresh.focus);
    setDraft("");
    setBusy(false);
    setWaiting(false);
    setStage(fresh.stage);
  };

  const askStarter = ({ text, focus: starterFocus }) => {
    // ponytail: send() re-checks these; guarding here keeps the leave fade from playing on a no-op tap.
    if (busy || abortRef.current) return;
    if (starterFocus) setFocus(starterFocus);
    setStartersLeaving(true);
    send(text, { focusService: starterFocus });
  };

  const pendingFormId = msgs.find((message) => message.form && !message.form.answered)?.form.callId ?? null;
  const unresolvedForm = pendingFormId !== null;
  // Ref, not closure: the blur fired by disabling the composer must see the new value.
  const unresolvedFormRef = useRef_Pill(false);
  unresolvedFormRef.current = unresolvedForm;
  // The form the disabled composer last pointed the Visitor at; cleared once
  // the pulse has played (or the form resolves).
  const [pulseFormId, setPulseFormId] = useState_Pill(null);
  useEffect_Pill(() => {
    if (!pulseFormId) return undefined;
    const t = setTimeout(() => setPulseFormId(null), 1200);
    return () => clearTimeout(t);
  }, [pulseFormId]);
  const revealPendingForm = () => {
    if (!pendingFormId) return;
    let reduce = false;
    try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch {}
    scrollRef.current?.querySelector?.(`[data-form-call-id="${pendingFormId}"]`)
      ?.scrollIntoView?.({ behavior: reduce ? "auto" : "smooth", block: "center" });
    // ponytail: a second tap inside the 1.2s pulse doesn't replay it; key the class by a counter if that matters.
    setPulseFormId(pendingFormId);
  };

  const send = async (text, opts = {}) => {
    const trimmed = (text || "").trim();
    if (!trimmed || readOnly || busy || abortRef.current || (unresolvedForm && !opts.resumeCallId)) return;
    if (NX_API.expireIfInactive()) {
      onExpired?.();
      return;
    }
    const turnId = ++turnIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    const focusService = opts.focusService ?? focus;
    pushUser(trimmed);
    setDraft("");
    setBusy(true);
    setWaiting(true);
    let firstToken = true;
    let receivedForm = false;
    const result = await NX_API.sendTurn({
      input: trimmed,
      focusService,
      resumeCallId: opts.resumeCallId,
      signal: controller.signal,
      onForm: (form) => {
        if (controller.signal.aborted) return;
        receivedForm = true;
        setWaiting(false);
        // Tokens stream before the form arrives; the text says "above", so insert the form ahead of it.
        setMsgs((messages) => {
          const formMessage = { role: "assistant", content: "", form: { ...form, answered: false }, turnId };
          const textIndex = messages.findIndex((message) => message.turnId === turnId && !message.form);
          if (textIndex === -1) return [...messages, formMessage];
          return [...messages.slice(0, textIndex), formMessage, ...messages.slice(textIndex)];
        });
      },
      onToken: (token) => {
        if (controller.signal.aborted) return;
        if (firstToken) {
          firstToken = false;
          setWaiting(false);
          setMsgs((messages) => [...messages, { role: "assistant", content: token, turnId }]);
          return;
        }
        setMsgs((messages) => messages.map((message) => (
          message.turnId === turnId && !message.form
            ? { ...message, content: message.content + token }
            : message
        )));
      },
    });
    const ownsRequest = abortRef.current === controller;
    if (ownsRequest) {
      abortRef.current = null;
      setBusy(false);
      setWaiting(false);
    }
    if (result.aborted || !ownsRequest) return;
    if (result.expired) {
      onExpired?.();
      return;
    }
    if (firstToken && !receivedForm) {
      pushNori(result.reply, consultationReplyMetadata(result, trimmed, focusService));
    } else if (result.retryable) {
      setMsgs((messages) => messages.map((message) => (
        message.turnId === turnId
          ? { ...message, retry: { input: trimmed, focusService } }
          : message
      )));
    }
    if (!result.fallback) {
      onSuccessfulSubmit?.();
      if (stage === "greet") setStage("discover");
    }
    onResponseComplete?.();
  };

  const retryTurn = ({ input, focusService }) => send(input, { focusService });

  const resolveForm = (callId, state) => {
    setPulseFormId(null);
    setMsgs((messages) => messages.map((message) => (
      message.form?.callId === callId
        ? { ...message, form: { ...message.form, answered: state } }
        : message
    )));
  };

  const submitForm = (form, answers) => {
    resolveForm(form.callId, "submitted");
    send(serializeConsultantFormAnswers(form, answers), { resumeCallId: form.callId });
  };

  const skipForm = (form) => resolveForm(form.callId, "skipped");

  const pickService = (svcId) => {
    const s = NX_SERVICES.find((x) => x.id === svcId);
    setFocus(svcId);
    send(s.cta, { focusService: svcId });
  };

  const recommend = (svcId) => {
    const s = NX_SERVICES.find((x) => x.id === svcId);
    pushNori(`Based on what you've described, ${s.title} is the closest fit.`, { rec: s });
    setStage("recommend");
  };

  const goHandoff = () => {
    pushNori("Happy to take it forward. Pick a route — all of them send your conversation to the team.", { handoff: true });
    setStage("handoff");
  };

  // Conversion: open the "send this brief to our team" form. Carries the live
  // transcript + chosen focus so the form can pre-summarise the request.
  const goBrief = () => {
    pushNori(
      "Great — I'll prepare a short consultation brief for the NEXT INNOVATIONS team. A few quick details so they can follow up:",
      { brief: { transcript: msgs, focus } }
    );
    setStage("handoff");
  };

  // Fallback recovery: re-surface the six main solutions as a picker so the
  // visitor can route themselves even if the AI reply failed.
  const chooseSolution = () => {
    pushNori("Here are our main solutions — pick the closest fit and I'll take it from there.", { services: true });
  };

  const presentationHidden = minimized || closing;

  return (
    <div
      className={`nx-consult-panel is-${presentationMode}${closing ? " is-closing" : ""}${minimized ? " is-minimized" : ""}`}
      role={presentationHidden ? undefined : "dialog"}
      aria-modal={presentationHidden ? undefined : "true"}
      aria-label="Next AI guided conversation"
      aria-hidden={presentationHidden}
      inert={presentationHidden ? "" : undefined}
      style={{
      position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
      width: "min(468px, calc(100vw - 32px))",
      maxHeight: "72vh",
      height: "min(640px, calc(100vh - 108px))",
      background: "#FFFCF6",
      borderRadius: 22,
      border: "1px solid var(--line)",
      boxShadow: "0 30px 72px -24px rgba(14,18,23,.34), 0 8px 20px -6px rgba(14,18,23,.14)",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
      zIndex: 3,
      pointerEvents: minimized ? "none" : "auto",
    }}>
      <PanelHeader onMinimize={onMinimize} onNewConsultation={startNewConsultation} accentName={accentName} stage={stage} logoSrc={logoSrc} />

      <div ref={scrollRef} className="nx-consult-body" style={{
        flex: 1, overflowY: "auto",
        padding: "18px 18px 8px",
        display: "flex", flexDirection: "column", gap: 12,
        background: "linear-gradient(180deg, #FFFCF6 0%, var(--paper-inset) 100%)",
      }}>
        {msgs.map((m, i) => (
          <div key={i} inert={readOnly ? "" : undefined}>
            <Turn m={m} logoSrc={logoSrc} onPickService={pickService} onRecommend={recommend}
                  onHandoff={goHandoff} onBrief={goBrief} onChoose={chooseSolution} onRetry={retryTurn}
                  onSubmitForm={submitForm} onSkipForm={skipForm} pulseFormId={pulseFormId} />
          </div>
        ))}

        {waiting && <ThinkingTurn logoSrc={logoSrc} />}

        {showStarters && !readOnly && (
          <div className={"nx-starters" + (startersLeaving ? " is-leaving" : "")} style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8, paddingBottom: 6 }}>
            <p className="nx-starter-greet" style={{ margin: "0 0 4px", fontSize: 13, color: "var(--fg-muted)" }}>Ask anything, or start with one of these:</p>
            {NX_STARTER_QUESTIONS.map((q) => (
              <button key={q.text} type="button" className="nx-starter-q" onClick={() => askStarter(q)} disabled={busy}
                style={{ textAlign: "left", font: "inherit", fontSize: 14, lineHeight: 1.35, padding: "10px 14px", borderRadius: 12, border: "1px solid var(--line)", background: "#FFFCF6", color: "var(--fg)", cursor: "pointer" }}>
                {q.text}
              </button>
            ))}
          </div>
        )}
      </div>

      {notice && (
        <div role="status" style={{ margin: "0 18px 12px", padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 10, background: "#FFFCF6", color: "var(--fg-muted)", fontSize: 13, lineHeight: 1.45 }}>
          {notice}
        </div>
      )}

      {readOnly && (
        <div role="status" style={{ margin: "0 18px 12px", padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 10, background: "#FFFCF6", color: "var(--fg-muted)", fontSize: 13, lineHeight: 1.45 }}>
          This consultation expired after 30 minutes of inactivity. The transcript is read-only.
          <button type="button" onClick={startNewConsultation} style={{ display: "block", marginTop: 8, border: 0, borderRadius: 8, padding: "8px 12px", background: "var(--ink)", color: "var(--on-ink)", font: "inherit", fontWeight: 600, cursor: "pointer" }}>Start new</button>
        </div>
      )}

      {!readOnly && <Composer
        value={draft}
        onChange={setDraft}
        onSend={() => send(draft)}
        onStop={stop}
        onFocus={onComposerFocus}
        onBlur={(payload) => onComposerBlur?.({ ...payload, formPending: unresolvedFormRef.current })}
        busy={busy}
        formPending={unresolvedForm}
        onRevealForm={revealPendingForm}
        focus={focus}
        accentName={accentName}
        inputRef={composerRef}
      />}
    </div>
  );
}

function PanelHeader({ onMinimize, onNewConsultation, stage, logoSrc = null }) {
  const stageLabels = {
    greet: "READY",
    discover: "LISTENING",
    recommend: "MATCH FOUND",
    handoff: "PICK A NEXT STEP",
  };
  return (
    <div className="nx-chead" style={{
      padding: "15px 16px",
      display: "flex", alignItems: "center", gap: 12,
      borderBottom: "1px solid var(--line-soft)",
      background: "#FFFCF6",
    }}>
      {logoSrc
        ? <img src={logoSrc} alt="Next AI" width={36} height={36} style={{ width: 36, height: 36, objectFit: "contain", flexShrink: 0 }} />
        : <NoriMark size={36} accent="oklch(72% 0.13 270)" bg="var(--ink)" />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
          Next AI Consultant
        </div>
        <div className="mono" style={{ fontSize: 10, color: "var(--fg-subtle)", letterSpacing: "0.08em", marginTop: 4, display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--moss)", boxShadow: "0 0 0 3px oklch(58% 0.10 158 / 0.2)" }} />
            {stageLabels[stage] || "READY"}
          </span>
          <span style={{ color: "var(--fg-faint)" }}>·</span>
          <span style={{ color: "var(--fg-faint)" }}>AI CONSULTATION · YGN / TYO</span>
        </div>
      </div>
      <button className="nx-consult-new" onClick={onNewConsultation} aria-label="New Consultation" title="New Consultation" style={{
        all: "unset", cursor: "pointer",
        width: 32, height: 32, borderRadius: 9,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--fg-subtle)",
        fontSize: 17,
        transition: "background var(--dur-snap), color var(--dur-snap)",
      }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-inset)"; e.currentTarget.style.color = "var(--fg)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--fg-subtle)"; }}
      >↻</button>
      <button onClick={onMinimize} aria-label="Minimize Consultation" title="Minimize Consultation" style={{
        all: "unset", cursor: "pointer",
        width: 32, height: 32, borderRadius: 9,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--fg-subtle)",
        fontSize: 19,
        transition: "background var(--dur-snap), color var(--dur-snap)",
      }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-inset)"; e.currentTarget.style.color = "var(--fg)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--fg-subtle)"; }}
      >×</button>
    </div>
  );
}

// Assistant avatar — PNG logo when a route supplies one, else the drawn mark.
function ConsultAvatar({ logoSrc, size = 24 }) {
  return logoSrc
    ? <img src={logoSrc} alt="Next AI" width={size} height={size} style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }} />
    : <NoriMark size={size} accent="oklch(72% 0.13 270)" bg="var(--ink)" />;
}

function ConsultantForm({ form, onSubmit, onSkip, pulse = false }) {
  const [answers, setAnswers] = useState_Pill(() => Object.fromEntries(
    form.fields.map((field) => [field.key, field.default ?? (field.type === "boolean" ? false : "")]),
  ));
  const locked = Boolean(form.answered);
  const setAnswer = (key, value) => setAnswers((current) => ({ ...current, [key]: value }));
  const fieldFontSize = useConsultFieldFontSize("inherit");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!locked) onSubmit(form, answers);
      }}
      data-form-call-id={form.callId}
      className={pulse ? "nx-consult-form-pulse" : undefined}
      style={{ padding: "12px 13px", background: "#FFFCF6", border: "1px solid var(--line)", borderRadius: "4px 14px 14px 14px" }}
    >
      {form.title && <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--fg)", marginBottom: 4 }}>{form.title}</div>}
      {form.message && <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--fg-muted)", marginBottom: 12 }}>{form.message}</div>}
      <fieldset disabled={locked} style={{ border: 0, padding: 0, margin: 0 }}>
        {form.fields.map((field) => (
          <label key={field.key} style={{ display: "block", marginTop: 10, fontSize: 12, fontWeight: 600, color: "var(--fg)" }}>
            {field.type === "boolean" ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(answers[field.key])}
                  onChange={(event) => setAnswer(field.key, event.target.checked)}
                />
                {field.label}
              </span>
            ) : (
              <>
                <span style={{ display: "block", marginBottom: 5 }}>{field.label}</span>
                {field.type === "select" ? (
                  <select
                    value={answers[field.key]}
                    onChange={(event) => setAnswer(field.key, event.target.value)}
                    style={{ width: "100%", padding: "8px 9px", border: "1px solid var(--line)", borderRadius: 8, background: "var(--paper-inset)", color: "var(--fg)", font: "inherit", fontSize: fieldFontSize }}
                  >
                    {!answers[field.key] && <option value="">Choose an option</option>}
                    {(Array.isArray(field.options) ? field.options : []).map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={answers[field.key]}
                    onChange={(event) => setAnswer(field.key, event.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 9px", border: "1px solid var(--line)", borderRadius: 8, background: "var(--paper-inset)", color: "var(--fg)", font: "inherit", fontSize: fieldFontSize }}
                  />
                )}
              </>
            )}
          </label>
        ))}
      </fieldset>
      <div style={{ display: "flex", gap: 8, marginTop: 13 }}>
        <button type="submit" disabled={locked} style={{ border: 0, borderRadius: 8, padding: "8px 12px", background: locked ? "var(--line)" : "var(--ink)", color: locked ? "var(--fg-subtle)" : "var(--on-ink)", font: "inherit", fontSize: 12, fontWeight: 600, cursor: locked ? "default" : "pointer" }}>
          {form.answered === "submitted" ? "Submitted" : form.submitLabel || "Submit"}
        </button>
        {!locked && <button type="button" onClick={() => onSkip(form)} style={{ border: 0, background: "transparent", color: "var(--fg-muted)", font: "inherit", fontSize: 12, cursor: "pointer" }}>Skip</button>}
        {form.answered === "skipped" && <span style={{ alignSelf: "center", color: "var(--fg-subtle)", fontSize: 12 }}>Skipped</span>}
      </div>
    </form>
  );
}

function Turn({ m, logoSrc, onPickService, onRecommend, onHandoff, onBrief, onChoose, onRetry, onSubmitForm, onSkipForm, pulseFormId = null }) {
  if (m.role === "user") {
    return (
      <div className="nx-turn" style={{ display: "flex", justifyContent: "flex-end", maxWidth: "90%", marginLeft: "auto" }}>
        <div style={{
          padding: "9px 13px",
          background: "var(--ink)",
          color: "var(--on-ink)",
          borderRadius: "14px 14px 4px 14px",
          fontSize: 13.5, lineHeight: 1.5,
        }}>{m.content}</div>
      </div>
    );
  }
  return (
    <div className="nx-turn" style={{ display: "flex", gap: 8, alignItems: "flex-start", maxWidth: "92%" }}>
      <ConsultAvatar logoSrc={logoSrc} />
      <div style={{ flex: 1 }}>
        {m.form ? (
          <ConsultantForm form={m.form} onSubmit={onSubmitForm} onSkip={onSkipForm} pulse={pulseFormId === m.form.callId} />
        ) : (
          <>
            {m.content && (
              <div className="nx-consult-markdown" style={{
                padding: "10px 13px",
                background: "#FFFCF6",
                border: "1px solid var(--line)",
                borderRadius: "4px 14px 14px 14px",
                fontSize: 13.5, lineHeight: 1.55, color: "var(--fg)",
              }}>
                <React.Suspense fallback={<span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>}>
                  <ConsultMarkdown>{m.content}</ConsultMarkdown>
                </React.Suspense>
              </div>
            )}
            {Array.isArray(m.media) && m.media.map((media, index) => media.type === "video" ? (
              <video key={`${media.url}-${index}`} src={media.url} controls preload="metadata" style={{ display: "block", width: "100%", marginTop: 8, borderRadius: 12 }} />
            ) : (
              <img key={`${media.url}-${index}`} src={media.url} alt={media.alt || "Consultant reply media"} loading="lazy" style={{ display: "block", width: "100%", height: "auto", marginTop: 8, borderRadius: 12 }} />
            ))}
          </>
        )}
        {m.retry && (
          <button
            type="button"
            onClick={() => onRetry(m.retry)}
            style={{
              all: "unset", cursor: "pointer", marginTop: 7,
              color: "var(--indigo-deep)", fontSize: 12, fontWeight: 600,
            }}
          >Retry response</button>
        )}
        {m.rec && <RecCardInChat s={m.rec} onBrief={onBrief} onHandoff={onHandoff} />}
        {m.handoff && <HandoffRow onBrief={onBrief} />}
        {m.fallback && <FallbackRow onChoose={onChoose} onBrief={onBrief} />}
        {m.services && <ServicePickRow onPick={onPickService} />}
        {m.brief && <BriefForm ctx={m.brief} />}
      </div>
    </div>
  );
}

// Meaningful "thinking" status — cycles through purposeful steps instead of a
// bare dot loop, with a subtle shimmer. Caps at the final step so a slow reply
// doesn't loop the copy. Reduced-motion users still get the readable label.
const NX_THINKING_STEPS = [
  "Thinking through your request…",
  "Matching your request to the right solution…",
  "Preparing a consultation brief…",
];
function ThinkingTurn({ logoSrc }) {
  const [idx, setIdx] = useState_Pill(0);
  useEffect_Pill(() => {
    const t = setInterval(() => setIdx((i) => Math.min(i + 1, NX_THINKING_STEPS.length - 1)), 1150);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <ConsultAvatar logoSrc={logoSrc} />
      <div style={{
        padding: "10px 13px",
        background: "#FFFCF6",
        border: "1px solid var(--line)",
        borderRadius: "4px 14px 14px 14px",
        display: "flex", alignItems: "center", gap: 9, minWidth: 0,
      }}>
        <span key={idx} className="nx-think-line" style={{
          fontSize: 13, lineHeight: 1.4, color: "var(--fg-muted)", fontWeight: 500,
        }}>{NX_THINKING_STEPS[idx]}</span>
        <TypingDots tone="light" />
      </div>
    </div>
  );
}

function RecCardInChat({ s, onBrief, onHandoff }) {
  return (
    <div style={{
      marginTop: 8,
      padding: 14,
      background: "var(--ink)",
      color: "var(--on-ink)",
      borderRadius: 14,
      boxShadow: "var(--shadow-2)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span className="mono" style={{
          fontSize: 9.5, letterSpacing: "0.1em",
          padding: "3px 8px", borderRadius: 999,
          background: "oklch(72% 0.13 270)", color: "#fff",
        }}>RECOMMENDED</span>
        <span style={{ color: "oklch(72% 0.13 270)" }}>
          <ServiceIcon id={s.icon} size={14} />
        </span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: "-0.012em", marginBottom: 6 }}>{s.title}</div>
      <div style={{ fontSize: 12.5, color: "var(--on-ink-muted)", lineHeight: 1.5, marginBottom: 10 }}>{s.body}</div>
      <div className="mono" style={{ fontSize: 10, color: "var(--on-ink-subtle)", letterSpacing: "0.06em", marginBottom: 12 }}>
        {s.examples.toUpperCase()}
      </div>
      <button onClick={onBrief} style={{
        all: "unset", cursor: "pointer",
        width: "100%",
        padding: "10px 14px",
        background: "var(--on-ink)", color: "var(--ink)",
        borderRadius: 10,
        fontSize: 13, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      }}>
        Send this brief to our team <span>→</span>
      </button>
      <button onClick={onHandoff} style={{
        all: "unset", cursor: "pointer",
        width: "100%", marginTop: 8, textAlign: "center",
        fontSize: 12, color: "var(--on-ink-subtle)",
      }}>
        Or see other ways to connect
      </button>
    </div>
  );
}

function HandoffRow({ onBrief }) {
  const actions = [
    // There is no contact page in this build, so every handoff runs through the
    // brief — it is the only path that actually reaches the team.
    { label: "Send this brief to our team", sub: "Reviewed by NEXT INNOVATIONS · reply in 1 business day", icon: "✦", primary: true, onClick: onBrief },
    { label: "Book a call",                 sub: "Send your preferred time in the brief",                   icon: "📅", onClick: onBrief },
  ];
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      {actions.map((a) => {
        const style = {
          padding: "10px 12px",
          background: a.primary ? "var(--ink)" : "#FFFCF6",
          color: a.primary ? "var(--on-ink)" : "var(--fg)",
          border: `1px solid ${a.primary ? "var(--ink)" : "var(--line)"}`,
          borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
          fontSize: 13, textDecoration: "none", cursor: "pointer",
        };
        const inner = (
          <>
          <span style={{ fontSize: 14 }}>{a.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{a.label}</div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 1 }}>{a.sub}</div>
          </div>
          <span style={{ fontSize: 12, opacity: 0.5 }}>→</span>
          </>
        );
        return a.href
          ? <a key={a.label} href={a.href} style={{ all: "unset", ...style }}>{inner}</a>
          : <button key={a.label} onClick={a.onClick} style={{ all: "unset", ...style }}>{inner}</button>;
      })}
    </div>
  );
}

// Offline / error recovery — keeps the conversion path alive when the AI
// service is unreachable: self-route to a solution, send a brief, or contact us.
function FallbackRow({ onChoose, onBrief }) {
  const actions = [
    { label: "Choose a solution",     sub: "Browse the six main solutions",         icon: "◇", onClick: onChoose },
    { label: "Send brief to our team", sub: "We'll follow up directly",             icon: "✦", primary: true, onClick: onBrief },
  ];
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      {actions.map((a) => {
        const inner = (
          <>
            <span style={{ fontSize: 14 }}>{a.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{a.label}</div>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 1 }}>{a.sub}</div>
            </div>
            <span style={{ fontSize: 12, opacity: 0.5 }}>→</span>
          </>
        );
        const css = {
          padding: "10px 12px",
          background: a.primary ? "var(--ink)" : "#FFFCF6",
          color: a.primary ? "var(--on-ink)" : "var(--fg)",
          border: `1px solid ${a.primary ? "var(--ink)" : "var(--line)"}`,
          borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
          fontSize: 13, textDecoration: "none", cursor: "pointer",
        };
        return a.href
          ? <a key={a.label} href={a.href} style={{ all: "unset", ...css }}>{inner}</a>
          : <button key={a.label} onClick={a.onClick} style={{ all: "unset", ...css }}>{inner}</button>;
      })}
    </div>
  );
}

// Six-solution picker used by the fallback recovery flow.
function ServicePickRow({ onPick }) {
  return (
    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
      {NX_MAIN_SERVICES.map((s) => (
        <button key={s.id} onClick={() => onPick(s.id)} style={{
          all: "unset", cursor: "pointer",
          padding: "8px 12px 8px 10px",
          background: "#FFFCF6", border: "1px solid var(--line)", borderRadius: 999,
          display: "inline-flex", alignItems: "center", gap: 7,
          fontSize: 12.5, fontWeight: 500, color: "var(--fg-muted)",
          transition: "border-color var(--dur-snap), color var(--dur-snap), background var(--dur-snap)",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.background = "var(--paper-inset)"; e.currentTarget.style.color = "var(--fg)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line)"; e.currentTarget.style.background = "#FFFCF6"; e.currentTarget.style.color = "var(--fg-muted)"; }}
        >
          <span style={{ color: "var(--indigo)", display: "inline-flex", flexShrink: 0 }}>
            <ServiceIcon id={s.icon} size={13} />
          </span>
          {s.title}
        </button>
      ))}
    </div>
  );
}

// ── Conversion brief form ─────────────────────────────────────────────
// Collects the contact details the team needs, shows an explicit confirm
// step ("Would you like our team to review this and contact you?"), and only
// then relays the consultation summary to the Slack "AI Consultant" group.
function buildBriefSummary(transcript, focus) {
  const svc = focus && NX_SERVICES.find((s) => s.id === focus);
  const turns = (transcript || [])
    .filter((m) => m.role === "user")
    .map((m) => m.content);
  return {
    solution: svc ? svc.title : "Not yet specified",
    request: turns.length ? turns.join("  ·  ") : "(started from the consultant — no message yet)",
  };
}

function BriefForm({ ctx }) {
  const summary = buildBriefSummary(ctx.transcript, ctx.focus);
  const [form, setForm] = useState_Pill({ name: "", company: "", contact: "", method: "Email", message: "" });
  const [phase, setPhase] = useState_Pill("collect"); // collect | confirm | sending | sent | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.contact.trim();

  const submit = async () => {
    setPhase("sending");
    try {
      await NX_API.sendBrief({
        ...form,
        focusService: ctx.focus || null,
        solution: summary.solution,
        request: summary.request,
        transcript: (ctx.transcript || []).map((m) => ({ role: m.role, content: m.content })),
        source: "next-ai-consultant",
      });
      setPhase("sent");
    } catch (_) {
      setPhase("error");
    }
  };

  const fieldFontSize = useConsultFieldFontSize(13);
  const fieldCss = {
    width: "100%", boxSizing: "border-box",
    padding: "9px 11px",
    background: "var(--paper-inset)", border: "1px solid var(--line)", borderRadius: 9,
    fontFamily: "inherit", fontSize: fieldFontSize, color: "var(--fg)", outline: "none",
  };
  const labelCss = { fontSize: 11, fontWeight: 600, color: "var(--fg-muted)", marginBottom: 5, display: "block" };

  const card = (children) => (
    <div style={{
      marginTop: 8, padding: 14,
      background: "#FFFCF6", border: "1px solid var(--line)", borderRadius: 14,
      boxShadow: "var(--shadow-1, 0 2px 8px -4px rgba(14,18,23,.12))",
    }}>{children}</div>
  );

  // Brief summary chip shown above the form / confirm.
  const summaryStrip = (
    <div style={{
      display: "flex", flexDirection: "column", gap: 4,
      padding: "9px 11px", marginBottom: 12,
      background: "var(--indigo-soft)", borderRadius: 9,
    }}>
      <div className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: "var(--indigo-deep)" }}>CONSULTATION BRIEF</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--fg)" }}>{summary.solution}</div>
      <div style={{ fontSize: 11.5, color: "var(--fg-muted)", lineHeight: 1.45 }}>{summary.request}</div>
    </div>
  );

  if (phase === "sent") {
    return card(
      <div style={{ textAlign: "center", padding: "6px 4px" }}>
        <div style={{
          width: 34, height: 34, borderRadius: 999, margin: "0 auto 10px",
          background: "var(--moss, oklch(58% 0.10 158))", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>✓</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Brief sent to the NEXT INNOVATIONS team</div>
        <div style={{ fontSize: 12.5, color: "var(--fg-muted)", lineHeight: 1.5 }}>
          Thanks, {form.name.split(" ")[0] || "there"} — we'll review it and reach out via {form.method.toLowerCase()} within one business day.
        </div>
      </div>
    );
  }

  if (phase === "confirm" || phase === "sending" || phase === "error") {
    return card(
      <>
        {summaryStrip}
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
          Would you like our team to review this and contact you?
        </div>
        <div style={{ fontSize: 12, color: "var(--fg-muted)", lineHeight: 1.5, marginBottom: 12 }}>
          {form.name}{form.company ? ` · ${form.company}` : ""} — {form.contact} ({form.method})
        </div>
        {phase === "error" && (
          <div style={{ fontSize: 12, color: "var(--vermilion, #b3402a)", marginBottom: 10 }}>
            Couldn't send just now. Please try again, or reach us at /contact.
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setPhase("collect")} disabled={phase === "sending"} style={{
            all: "unset", cursor: "pointer", padding: "9px 14px", borderRadius: 9,
            border: "1px solid var(--line)", fontSize: 12.5, color: "var(--fg-muted)",
          }}>Back</button>
          <button onClick={submit} disabled={phase === "sending"} style={{
            all: "unset", cursor: phase === "sending" ? "default" : "pointer",
            flex: 1, textAlign: "center", padding: "9px 14px", borderRadius: 9,
            background: "var(--ink)", color: "var(--on-ink)", fontSize: 12.5, fontWeight: 600,
            opacity: phase === "sending" ? 0.75 : 1,
          }}>
            {phase === "sending" ? "Sending…" : "Send to NEXT INNOVATIONS team"}
          </button>
        </div>
      </>
    );
  }

  // collect
  return card(
    <>
      {summaryStrip}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelCss}>Name *</label>
          <input value={form.name} onChange={set("name")} placeholder="Your name" style={fieldCss} />
        </div>
        <div>
          <label style={labelCss}>Company</label>
          <input value={form.company} onChange={set("company")} placeholder="Optional" style={fieldCss} />
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelCss}>Email or phone *</label>
        <input value={form.contact} onChange={set("contact")} placeholder="you@company.com / +95…" style={fieldCss} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelCss}>Preferred contact method</label>
        <div style={{ display: "flex", gap: 6 }}>
          {["Email", "Phone", "Messenger"].map((opt) => {
            const on = form.method === opt;
            return (
              <button key={opt} onClick={() => setForm((f) => ({ ...f, method: opt }))} style={{
                all: "unset", cursor: "pointer", flex: 1, textAlign: "center",
                padding: "7px 0", borderRadius: 8, fontSize: 12, fontWeight: 500,
                background: on ? "var(--ink)" : "#FFFCF6",
                color: on ? "var(--on-ink)" : "var(--fg-muted)",
                border: `1px solid ${on ? "var(--ink)" : "var(--line)"}`,
              }}>{opt}</button>
            );
          })}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelCss}>Anything to add</label>
        <textarea value={form.message} onChange={set("message")} rows={2} placeholder="Optional — timeline, budget, context…"
          style={{ ...fieldCss, resize: "none", lineHeight: 1.5 }} />
      </div>
      <button onClick={() => valid && setPhase("confirm")} disabled={!valid} style={{
        all: "unset", cursor: valid ? "pointer" : "default",
        width: "100%", boxSizing: "border-box", textAlign: "center",
        padding: "10px 14px", borderRadius: 10,
        background: valid ? "var(--ink)" : "var(--line)",
        color: valid ? "var(--on-ink)" : "var(--fg-subtle)",
        fontSize: 13, fontWeight: 600,
      }}>
        Review &amp; send brief
      </button>
      <div className="mono" style={{ fontSize: 9.5, color: "var(--fg-subtle)", letterSpacing: "0.04em", marginTop: 9, textAlign: "center" }}>
        YOU'LL CONFIRM BEFORE ANYTHING IS SENT
      </div>
    </>
  );
}

const NX_FORM_PENDING_PLACEHOLDER = "Answer or skip the form above to continue";

function Composer({ value, onChange, onSend, onStop, onFocus, onBlur, busy, formPending, onRevealForm, focus, accentName, inputRef }) {
  const [composerFocused, setComposerFocused] = useState_Pill(false);
  const typedPlaceholder = useNXTypedPlaceholder(!value && !composerFocused && !formPending);
  const sendLive = !formPending && (busy || value.trim());
  const composerFontSize = useConsultFieldFontSize(13.5);
  return (
    <div style={{
      padding: "10px 12px 12px",
      borderTop: "1px solid var(--line-soft)",
      background: "#FFFCF6",
    }}>
      {focus && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "3px 8px", marginBottom: 8,
          background: "var(--indigo-soft)", color: "var(--indigo-deep)",
          borderRadius: 999, fontSize: 11, fontWeight: 500,
        }}>
          <span style={{ color: "var(--indigo-deep)" }}><ServiceIcon id={NX_SERVICES.find((s) => s.id === focus)?.icon} size={11} /></span>
          {NX_SERVICES.find((s) => s.id === focus)?.title}
          <button onClick={() => onChange("")} style={{ all: "unset", cursor: "pointer", marginLeft: 2, fontSize: 13, lineHeight: 1 }}>×</button>
        </div>
      )}
      <div
        // A disabled input swallows events, so the wrapper routes the tap to the form.
        onClick={formPending ? onRevealForm : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 6px 6px 14px",
          background: "var(--paper-inset)",
          border: "1px solid var(--line)",
          borderRadius: 12,
          cursor: formPending ? "pointer" : undefined,
        }}>
        <input
          ref={inputRef}
          aria-label={`Message ${accentName}`}
          value={value}
          disabled={formPending}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          onFocus={() => {
            setComposerFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setComposerFocused(false);
            onBlur?.({ draft: value, busy });
          }}
          placeholder={formPending ? NX_FORM_PENDING_PLACEHOLDER : value ? "" : typedPlaceholder}
          style={{
            flex: 1, background: "transparent", border: 0, outline: "none",
            fontFamily: "inherit", fontSize: composerFontSize, color: "var(--fg)", padding: "6px 0",
          }}
        />
        <button
          type="button"
          onClick={busy ? onStop : onSend}
          disabled={!busy && !value.trim()}
          aria-label={busy ? "Stop response" : "Send message"}
          className={busy ? "nx-consult-stop" : undefined}
          style={{
          all: "unset", cursor: sendLive ? "pointer" : "default",
          pointerEvents: formPending ? "none" : undefined,
          width: 30, height: 30, borderRadius: 8,
          background: sendLive ? "var(--ink)" : "var(--line)",
          color: sendLive ? "var(--on-ink)" : "var(--fg-subtle)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14,
          transition: "all var(--dur-snap)",
        }}>{busy ? <span aria-hidden="true" className="nx-consult-stop-icon" /> : "↑"}</button>
      </div>
      <div className="mono" style={{ fontSize: 9.5, color: "var(--fg-subtle)", letterSpacing: "0.06em", marginTop: 7, paddingLeft: 4, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        <span>↵ send</span><span>·</span><span>RAG-connected</span>
      </div>
    </div>
  );
}

/* ───── Seed transcripts for the canvas's static states ──────── */

export function seedTranscript(state) {
  if (state === "greet") {
    return { msgs: [], stage: "greet", focus: null };
  }
  if (state === "discover") {
    return {
      msgs: [
        { role: "assistant", content: "Hi! I'm Next AI. What brings you here today?" },
        { role: "user", content: "We need an AI chatbot for customer support" },
        { role: "assistant", content: "Got it — AI Business Solutions. To shape the recommendation, what channels matter most: WhatsApp, Messenger, website chat, or all of the above?" },
        { role: "user", content: "WhatsApp first, web chat second. Volume is hundreds a day." },
        { role: "assistant", content: "Clear. One more — is the goal mostly handling repeat questions, or routing to human agents faster?" },
      ],
      stage: "discover",
      focus: "ai",
    };
  }
  if (state === "recommend") {
    const ai = NX_SERVICES.find((s) => s.id === "ai");
    return {
      msgs: [
        { role: "assistant", content: "Hi! I'm Next AI. What brings you here today?" },
        { role: "user", content: "We need an AI chatbot for customer support" },
        { role: "assistant", content: "Got it — AI Business Solutions. What channels matter most?" },
        { role: "user", content: "WhatsApp first, web chat second. Volume is hundreds a day." },
        { role: "assistant", content: "Based on what you've described, AI Business Solutions is the closest fit.", rec: ai },
      ],
      stage: "recommend",
      focus: "ai",
    };
  }
  if (state === "handoff") {
    const ai = NX_SERVICES.find((s) => s.id === "ai");
    return {
      msgs: [
        { role: "assistant", content: "Hi! I'm Next AI. What brings you here today?" },
        { role: "user", content: "We need an AI chatbot for customer support" },
        { role: "assistant", content: "Got it — AI Business Solutions. What channels matter most?" },
        { role: "user", content: "WhatsApp first, web chat second. Hundreds of messages a day." },
        { role: "assistant", content: "Based on what you've described, AI Business Solutions is the closest fit.", rec: ai },
        { role: "user", content: "Looks right. How do we start?" },
        { role: "assistant", content: "Happy to take it forward. Pick a route — all of them send your conversation to the team.", handoff: true },
      ],
      stage: "handoff",
      focus: "ai",
    };
  }
  return { msgs: [], stage: "greet", focus: null };
}

export { PillLauncher, ConsultPanel };
